import { Outlet } from "react-router";
import AppHeader from "./AppHeader";
import AppSidebar from "./AppSidebar";
import { SidebarProvider } from "../context/SidebarProvider";
import { useSidebar } from "../context/useSidebar";
import Backdrop from "./Backdrop";
import MfaGdprModal from "../shared/ui/common/MfaGdprModal";
import OnboardingModal from "../modules/onboarding/ui/OnboardingModal";
import { InteractiveTrainingTour } from "../shared/components/tour/InteractiveTrainingTour";
import { useTherapist } from "../context/TherapistProvider";
import { TherapistAPI } from "../modules/therapist/infrastructure/api/therapist.api";
import { useBreakpoint } from "../hooks/useBreakpoint";
import BottomBar from "../shared/ui/BottomBar";
import { LAYOUT } from "../shared/layout";
import AlchelyChatFAB from "../shared/components/AlchelyChatFAB";
import { useOnboardingEligibility } from "../modules/onboarding/application/useOnboardingEligibility";
import CenterPaymentGate from "../modules/center_admin/ui/components/CenterPaymentGate";
import CenterVocabulary from "../modules/center_admin/ui/components/CenterVocabulary";
import AnnouncementsModal from "../modules/announcements/ui/AnnouncementsModal";
import PastDueNotice from "../modules/subscription/ui/components/PastDueNotice";
import { usePendingAnnouncements } from "../modules/announcements/application/useAnnouncements";

const LayoutContent: React.FC = () => {
  const { isExpanded, isHovered, isMobileOpen } = useSidebar();
  const { isSmallScreen } = useBreakpoint();
  const { therapist, setTherapist } = useTherapist();

  const handleTourComplete = async () => {
    if (therapist) {
      try {
        await TherapistAPI.update(therapist.id, { has_completed_training: true });
        setTherapist({ ...therapist, has_completed_training: true });
      } catch (error) {
        console.error("Failed to update training state", error);
      }
    }
  };

  // First-run surfaces run one at a time: onboarding, then the tour, then MFA.
  // Onboarding leads because it decides which tools the workspace even has.
  // The predicate is the modal's own, so the two can never disagree.
  const shouldRunOnboarding = useOnboardingEligibility();
  const shouldRunTour =
    therapist !== null && therapist.has_completed_training === false && !shouldRunOnboarding;

  // Platform news comes after both: what changed in Alchely means little to
  // someone still being shown around it. The MFA reminder waits until the news
  // has been looked up and dismissed, so the two never stack.
  const canShowAnnouncements =
    therapist !== null && therapist.has_completed_training === true && !shouldRunOnboarding;
  const announcements = usePendingAnnouncements(canShowAnnouncements);
  const holdMfaModal =
    shouldRunOnboarding || !announcements.isSettled || announcements.pending.length > 0;

  return (
    <>
      {/* A training centre calls its people trainers and students. Words
          only — mounted once here so every screen gets them. */}
      <CenterVocabulary />
      <OnboardingModal />
      {canShowAnnouncements && <AnnouncementsModal pending={announcements.pending} />}
      {!holdMfaModal && <MfaGdprModal />}
      <InteractiveTrainingTour run={shouldRunTour} onTourComplete={handleTourComplete} />
      <div className="flex h-screen overflow-hidden bg-surface-base mesh-gradient">
        {!isSmallScreen && <AppSidebar />}
        <Backdrop />
        <div
          className={`flex-1 min-w-0 flex flex-col h-full transition-[margin] duration-[--duration-slow] ease-[--ease-standard]
          ${!isSmallScreen && (isExpanded || isHovered) ? "xl:ml-[280px]" : ""}
          ${!isSmallScreen && !isExpanded && !isHovered ? "xl:ml-[88px]" : ""}
          ${isMobileOpen ? "ml-0" : ""}`}
          style={isSmallScreen ? { paddingBottom: LAYOUT.NAV_HEIGHT.BOTTOM_BAR } : {}}
        >
          <AppHeader />
          <main className="flex-1 overflow-y-auto no-scrollbar relative w-full h-full">
            <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
              {/* A centre that has bought no seats has no product behind
                  this: its administrator is sent to the one screen that can
                  change that. */}
              {/* A declined renewal, said wherever they are: the plan holds
                  while Stripe retries, so it is easy to miss until it stops. */}
              <PastDueNotice />
              <CenterPaymentGate>
                <Outlet />
              </CenterPaymentGate>
            </div>
          </main>
        </div>
        {isSmallScreen && <BottomBar />}
        <AlchelyChatFAB />
      </div>
    </>
  );
};

const AppLayout: React.FC = () => {
  return (
    <SidebarProvider>
      <LayoutContent />
    </SidebarProvider>
  );
};

export default AppLayout;
